import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { componentMasterAPI, productVerticalMasterAPI, complaintCategoryMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditBusinessVerticalMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeComponent = (item) => ({
  componentCode: item.componentCode || item.ComponentCode || item.COMPONANTCODE || "",
  componentId: item.componentId || item.ComponentId || item.COMPONANTID || "",
  componentName: item.componentName || item.ComponentName || item.COMPONANTNAME || "",
  componentCatgCode: item.componentCatgCode || item.ComponentCatgCode || item.COMPONANTCATGCODE || "",
  bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditComponentMaster = () => {
  const navigate = useNavigate();
  const { componentCode } = useParams();
  const isEditMode = !!componentCode;

  const routePermissions = getRoutePermissions("/components");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [component, setComponent] = useState({
    componentCode: "",
    componentId: "",
    componentName: "",
    componentCatgCode: "",
    bvCode: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [componentCategories, setComponentCategories] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} components.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/components"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadComponent();
    } else {
      loadNextComponentCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentCode, canSave]);

  const loadDropdowns = async () => {
    setLoadingDropdowns(true);
    try {
      // Load Component Categories
      try {
        const catResponse = await complaintCategoryMasterAPI.getPaged({
          status: "active",
          pageSize: 1000,
        });
        if (catResponse?.success && catResponse.data) {
          const items = catResponse.data.categories || catResponse.data.Categories || [];
          const categories = items.map(item => ({
            code: item.compCatgCode || item.CompCatgCode || item.COMPONANTCATGCODE || "",
            name: item.compCatgName || item.CompCatgName || item.COMPONANTCATGNAME || item.compCatgCode || item.CompCatgCode || ""
          }));
          setComponentCategories(categories);
        }
      } catch (e) {
        console.error("Error loading component categories:", e);
      }

      // Load Business Verticals
      try {
        const bvResponse = await productVerticalMasterAPI.getPaged({
          status: "active",
          pageSize: 1000,
        });
        if (bvResponse?.success && bvResponse.data) {
          const items = bvResponse.data.productVerticals || bvResponse.data.ProductVerticals || [];
          const verticals = items.map(item => ({
            code: item.bvCode || item.BVCode || item.BVCODE,
            name: item.bvName || item.BVName || item.BVNAME || item.bvCode || item.BVCode || item.BVCODE
          }));
          setBusinessVerticals(verticals);
        }
      } catch (e) {
        console.error("Error loading business verticals:", e);
      }
    } catch (e) {
      console.error("Error loading dropdowns:", e);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadNextComponentCode = async () => {
    try {
      const response = await componentMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setComponent((prev) => ({ ...prev, componentCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next component code:", e);
      setComponent((prev) => ({ ...prev, componentCode: "COMP00001" }));
    }
  };

  const loadComponent = async () => {
    try {
      setLoading(true);
      const response = await componentMasterAPI.getByCode(componentCode);
      if (response && response.success && response.data) {
        const normalized = normalizeComponent(response.data);
        setComponent(normalized);
      } else {
        navigate("/components");
      }
    } catch (e) {
      console.error("Error loading component:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load component details", "error");
      }
      navigate("/components");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setComponent((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!component.componentCode?.trim()) {
      Swal.fire("Error", "Component Code is required", "error");
      return false;
    }
    if (!component.componentId?.trim()) {
      Swal.fire("Error", "Component ID is required", "error");
      return false;
    }
    if (!component.componentName?.trim()) {
      Swal.fire("Error", "Component Name is required", "error");
      return false;
    }
    if (!component.componentCatgCode?.trim()) {
      Swal.fire("Error", "Component Category is required", "error");
      return false;
    }
    if (!component.bvCode?.trim()) {
      Swal.fire("Error", "Business Vertical is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await componentMasterAPI.update(
          componentCode,
          component,
          createdBy
        );
      } else {
        response = await componentMasterAPI.create(component, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Component saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/components");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save component",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving component:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save component. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/components"
            onClick={(e) => {
              e.preventDefault();
              navigate("/components");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Component Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Component" : "Add Component"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/components")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: 'none' }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Code <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={component.componentCode}
                  required
                  fullWidth
                  disabled={isEditMode}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component ID <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={component.componentId}
                  onChange={(e) => handleChange("componentId", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter component ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Name <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={component.componentName}
                  onChange={(e) => handleChange("componentName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter component name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Category <span className="required-asterisk">*</span>
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={component.componentCatgCode}
                    onChange={(e) => handleChange("componentCatgCode", e.target.value)}
                    displayEmpty
                    className="add-edit-product-textfield"
                    size="small"
                  >
                    <MenuItem value="" disabled>
                      {loadingDropdowns ? "Loading..." : "Select Component Category"}
                    </MenuItem>
                    {componentCategories.map((cat) => (
                      <MenuItem key={cat.code} value={cat.code}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Business Vertical <span className="required-asterisk">*</span>
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={component.bvCode}
                    onChange={(e) => handleChange("bvCode", e.target.value)}
                    displayEmpty
                    className="add-edit-product-textfield"
                    size="small"
                  >
                    <MenuItem value="" disabled>
                      {loadingDropdowns ? "Loading..." : "Select Business Vertical"}
                    </MenuItem>
                    {businessVerticals.map((bv) => (
                      <MenuItem key={bv.code} value={bv.code}>
                        {bv.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/components")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditComponentMaster;
